import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * Tailwind's merge, told about this system's own font sizes.
 *
 * It has to be told. `tailwind-merge` decides which group a class belongs to
 * from its name, and its rule for `text-*` is: a t-shirt size or an explicit
 * length is a font size, and **anything else is a colour**. So `text-ui`
 * looked like a colour, and `cn("… text-ui …", "text-ink")` resolved two
 * "colours" in favour of the last one and dropped the size on the floor.
 *
 * That failed silently and everywhere at once. The nav rows, the prompt box
 * and the credit chip all kept the size they inherited — 16px, 12px, 12px —
 * while their class attributes still read `text-ui`, `text-prose`,
 * `text-micro`, so the markup said one thing and the screen showed another.
 * The only reason the page heading came out right is that it does not go
 * through `cn` at all.
 *
 * Keep this list in step with the `--text-*` tokens in `tokens.css`. A name
 * added there and not here is a size that will be dropped the moment somebody
 * sets a colour beside it.
 */
const merge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        { text: ["figure", "stat", "display", "title", "prose", "ui", "meta", "micro"] },
      ],
    },
  },
});

/** Merge class names, letting a caller's utility win over a variant default. */
export function cn(...inputs: ClassValue[]): string {
  return merge(clsx(inputs));
}
